import { fetchBodyCacheStatus, type BodyCacheStatusResponse } from "@/lib/api/imap";
import type { ApiResult } from "@/lib/api/client";
import { INBOX_CACHE_WINDOW_SIZE } from "@/lib/constants/inbox";

const MIN_GAP_MS = 4_000;

const inflight = new Map<string, Promise<ApiResult<BodyCacheStatusResponse>>>();
const recentOk = new Map<string, { at: number; flags: Record<string, boolean> }>();

function normalizeUids(uids: number[]): number[] {
  const unique = new Set<number>();
  for (const uid of uids) {
    if (uid > 0) unique.add(uid);
  }
  return [...unique].slice(0, INBOX_CACHE_WINDOW_SIZE);
}

function requestKey(accountId: number, folder: string, uids: number[]): string {
  const normalized = normalizeUids(uids);
  return `${accountId}\0${folder}\0${normalized.join(",")}`;
}

/** Évite la tempête bodies-status (ERR_INSUFFICIENT_RESOURCES). */
export async function fetchBodyCacheStatusCoordinated(params: {
  accountId: number;
  folder: string;
  uids: number[];
  /** Ignore le cache court (poll après warm queue). */
  force?: boolean;
}): Promise<ApiResult<BodyCacheStatusResponse>> {
  const uids = normalizeUids(params.uids);
  if (uids.length === 0) {
    return {
      ok: true,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        flags: {},
      },
    };
  }

  const key = requestKey(params.accountId, params.folder, uids);
  const now = Date.now();
  const cached = recentOk.get(key);
  if (!params.force && cached && now - cached.at < MIN_GAP_MS) {
    return {
      ok: true,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        flags: cached.flags,
      },
    };
  }

  const running = inflight.get(key);
  if (running) {
    return running;
  }

  const promise = fetchBodyCacheStatus({
    accountId: params.accountId,
    folder: params.folder,
    uids,
  }).then((res) => {
    inflight.delete(key);
    if (res.ok) {
      const flags = res.data.flags ?? {};
      const anyReady = Object.values(flags).some(Boolean);
      // Ne pas figer des « nuage » 4 s : les retries doivent revoir DD02.
      if (anyReady || params.force) {
        recentOk.set(key, { at: Date.now(), flags });
      }
    }
    return res;
  });

  inflight.set(key, promise);
  return promise;
}
